import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Seo, breadcrumbLd } from "../components/Seo";
import { fetchBlogPost } from "../lib/api";
import { ArrowLeft, Calendar, User } from "@phosphor-icons/react";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

// Article-schema JSON-LD emitted per post — earns Google's Article rich
// result + surfaces the post to Discover with its featured image.
function articleLd(post) {
  if (!post) return null;
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.seo_title || post.title,
    description: post.seo_description || post.excerpt,
    image: post.featured_image_url ? [post.featured_image_url] : undefined,
    author: { "@type": "Organization", name: post.author_name || "Flycher Team" },
    publisher: {
      "@type": "Organization",
      name: "Flycher",
      logo: {
        "@type": "ImageObject",
        url: "https://flycher.com/logo512.png",
      },
    },
    datePublished: post.published_at,
    dateModified: post.updated_at || post.published_at,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://flycher.com/blog/${post.slug}`,
    },
  };
}

const mdComponents = {
  // Ensure internal links use React Router (client-side)
  a({ href, children, ...props }) {
    if (href && href.startsWith("/")) {
      return (
        <Link to={href} {...props}>
          {children}
        </Link>
      );
    }
    return (
      <a href={href} target="_blank" rel="noreferrer" {...props}>
        {children}
      </a>
    );
  },
};

export default function BlogPost() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    fetchBlogPost(slug)
      .then((p) => {
        if (cancelled) return;
        setPost(p);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setNotFound(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const jsonLd = post
    ? [
        articleLd(post),
        breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog" },
          { name: post.title, path: `/blog/${post.slug}` },
        ]),
      ]
    : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <Navbar />
        <main className="flex-1 max-w-3xl mx-auto w-full px-5 md:px-8 py-16 animate-pulse">
          <div className="h-4 w-32 bg-slate-200 rounded mb-6" />
          <div className="h-12 w-3/4 bg-slate-200 rounded mb-4" />
          <div className="h-6 w-1/2 bg-slate-200 rounded mb-8" />
          <div className="h-64 bg-slate-200 rounded-xl mb-8" />
          <div className="space-y-3">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-4 bg-slate-200 rounded" />
            ))}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <Seo
          title="Post not found"
          description="This travel guide could not be found."
          path={`/blog/${slug}`}
          noindex
        />
        <Navbar />
        <main className="flex-1 max-w-2xl mx-auto w-full px-5 py-24 text-center">
          <h1 className="text-3xl font-serif text-slate-900 mb-3">Post not found</h1>
          <p className="text-slate-600 mb-8">
            The article you were looking for isn&apos;t here — maybe it was moved. Browse other travel guides instead.
          </p>
          <button
            type="button"
            onClick={() => nav("/blog")}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-orange-600 text-white font-medium hover:bg-orange-700 transition"
            data-testid="blog-back-to-list"
          >
            <ArrowLeft size={16} weight="bold" />
            Back to blog
          </button>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="blog-detail-page">
      <Seo
        title={post.seo_title || post.title}
        description={post.seo_description || post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.featured_image_url}
        type="article"
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="flex-1 pb-16">
        {/* Hero */}
        <div className="max-w-3xl mx-auto w-full px-5 md:px-8 pt-8 md:pt-12">
          <Link
            to="/blog"
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-orange-600 mb-6 transition-colors"
            data-testid="blog-back-link"
          >
            <ArrowLeft size={14} weight="bold" />
            All guides
          </Link>
          <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mb-3">
            {post.published_at && (
              <span className="inline-flex items-center gap-1.5">
                <Calendar size={12} weight="bold" />
                {fmtDate(post.published_at)}
              </span>
            )}
            {post.author_name && (
              <span className="inline-flex items-center gap-1.5">
                <User size={12} weight="bold" />
                {post.author_name}
              </span>
            )}
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif text-slate-900 leading-[1.1] mb-4">
            {post.title}
          </h1>
          {post.excerpt && (
            <p className="text-base md:text-lg text-slate-600 leading-relaxed">
              {post.excerpt}
            </p>
          )}
        </div>

        {/* Featured image */}
        {post.featured_image_url && (
          <div className="max-w-4xl mx-auto w-full px-5 md:px-8 mt-8 md:mt-10">
            <div className="aspect-[16/9] rounded-2xl overflow-hidden bg-slate-200 shadow-sm">
              <img
                src={post.featured_image_url}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        )}

        {/* Body */}
        <article
          className="max-w-3xl mx-auto w-full px-5 md:px-8 mt-10 md:mt-14 prose prose-slate prose-lg
                     prose-headings:font-serif prose-headings:text-slate-900
                     prose-h1:hidden prose-h2:mt-12 prose-h2:mb-4 prose-h2:text-2xl md:prose-h2:text-3xl
                     prose-h3:mt-8 prose-h3:mb-3 prose-h3:text-xl
                     prose-p:text-slate-700 prose-p:leading-relaxed
                     prose-a:text-orange-600 prose-a:no-underline hover:prose-a:underline
                     prose-strong:text-slate-900
                     prose-blockquote:border-l-orange-500 prose-blockquote:bg-orange-50/50 prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:rounded-r-lg prose-blockquote:not-italic
                     prose-table:text-sm prose-th:bg-slate-100 prose-th:text-slate-900
                     prose-img:rounded-xl"
          data-testid="blog-post-body"
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={mdComponents}
          >
            {post.body_markdown || post.body_html || ""}
          </ReactMarkdown>
        </article>

        {/* CTA footer */}
        <div className="max-w-3xl mx-auto w-full px-5 md:px-8 mt-16">
          <div className="rounded-2xl bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 p-6 md:p-8 text-center">
            <p className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-2">
              Ready to plan?
            </p>
            <h3 className="text-2xl md:text-3xl font-serif text-slate-900 mb-3">
              Build your itinerary in 60 seconds
            </h3>
            <p className="text-slate-600 mb-6 max-w-lg mx-auto">
              Free AI travel planner for Indians. INR pricing, real visa rules, honest budgets.
            </p>
            <Link
              to="/itinerary"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-orange-600 text-white font-semibold hover:bg-orange-700 transition"
              data-testid="blog-cta-itinerary"
            >
              Open AI Planner
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
